// src/components/ServicesShortcuts.jsx
import React from "react";

/**
 * Drop-in shortcuts section (no MUI).
 * - Responsive, accessible grid of cards
 * - Works with or without icon React nodes
 * - Unique CSS namespace: .mrm-sc*
 *
 * Usage:
 * <ServicesShortcuts items={services} title="Quick access" />
 * // where `services` can include an `icon` React node or omit it
 */

const fallbackIcons = {
  Buyers: (props) => (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path fill="currentColor" d="M16 11c1.66 0 3-1.57 3-3.5S17.66 4 16 4s-3 1.57-3 3.5S14.34 11 16 11zm-8 0c1.66 0 3-1.57 3-3.5S9.66 4 8 4 5 5.57 5 7.5 6.34 11 8 11zm0 2c-2.33 0-7 1.17-7 3.5V20h14v-3.5C15 14.17 10.33 13 8 13zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.93 1.97 3.45V20h7v-3.5c0-2.33-4.67-3.5-7-3.5z"/>
    </svg>
  ),
  Sellers: (props) => (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path fill="currentColor" d="M3 3h18v2H3V3zm2 4h14l-1.5 12.5a2 2 0 0 1-2 1.5H8.5a2 2 0 0 1-2-1.5L5 7zm7 3a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"/>
    </svg>
  ),
  Properties: (props) => (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path fill="currentColor" d="M12 3l9 7h-2v9a1 1 0 0 1-1 1h-5v-6H11v6H6a1 1 0 0 1-1-1v-9H3l9-7z"/>
    </svg>
  ),
  Landlords: (props) => (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path fill="currentColor" d="M10 2h4l1 3h5a1 1 0 0 1 1 1v3h-2V7h-4v3H9V7H5v2H3V6a1 1 0 0 1 1-1h5l1-3zM4 11h16l-1.2 8.4A2 2 0 0 1 16.82 21H7.18a2 2 0 0 1-1.98-1.6L4 11zm8 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"/>
    </svg>
  ),
  Tenants: (props) => (
    <svg viewBox="0 0 24 24" aria-hidden="true" {...props}>
      <path fill="currentColor" d="M12 3l8 6v12h-6v-6H10v6H4V9l8-6zm-2 8h4v2h-4v-2z"/>
    </svg>
  ),
};

const defaultItems = [
  { label: "Buyers", href: "/buyers" },
  { label: "Sellers", href: "/sellers" },
  { label: "Properties", href: "/properties" },
  { label: "Landlords", href: "/landlords" },
  { label: "Tenants", href: "/tenants" },
];

const accents = ["#1e3a8a", "#0ea5e9", "#7c3aed", "#16a34a", "#b91c1c"];

export default function ServicesShortcuts({ items = defaultItems, title = "Quick shortcuts" }) {
  return (
    <>
      <style>{`
        /* ===== Shortcuts section (namespaced) ===== */
        .mrm-sc { --ink:#0f172a; --muted:#475569; --line:#e5e7eb; --paper:#ffffff; --bg:#f8fafc; font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial; color:var(--ink); }
        .mrm-sc__wrap { max-width: 1200px; margin: 0 auto; padding: 3rem 1rem; }
        .mrm-sc__head { display:flex; align-items:flex-end; justify-content:space-between; gap:.75rem; margin-bottom:1.25rem; }
        .mrm-sc__title { margin:0; font-weight:900; font-size: clamp(1.25rem, 2.6vw, 1.75rem); }
        .mrm-sc__muted { color: var(--muted); font-size:.95rem; }

        .mrm-sc__grid { display:grid; grid-template-columns: 1fr; gap:.9rem; }
        @media (min-width: 560px){ .mrm-sc__grid { grid-template-columns: repeat(2, 1fr); } }
        @media (min-width: 820px){ .mrm-sc__grid { grid-template-columns: repeat(3, 1fr); } }
        @media (min-width: 1080px){ .mrm-sc__grid { grid-template-columns: repeat(5, 1fr); } }

        .mrm-sc__card {
          position:relative;
          display:flex; align-items:center; gap:.9rem;
          min-height: 96px;
          background: var(--paper);
          border: 1px solid var(--line);
          border-radius: 14px;
          padding: .9rem 1rem;
          text-decoration:none; color:inherit;
          box-shadow: 0 6px 18px rgba(2,6,23,.05);
          transition: transform .18s ease, box-shadow .18s ease, border-color .18s ease;
        }
        .mrm-sc__card:focus-visible { outline: 3px solid rgba(14,165,233,.45); outline-offset: 2px; }
        .mrm-sc__card:hover { transform: translateY(-3px); box-shadow: 0 14px 28px rgba(2,6,23,.10); border-color: #dbe2ea; }

        .mrm-sc__iconwrap {
          --accent:#1e3a8a;
          width: 48px; height: 48px; min-width:48px;
          border-radius: 12px;
          background: rgba(30,58,138,.08);
          color: var(--accent);
          display:grid; place-items:center;
        }
        .mrm-sc__iconwrap svg { width: 26px; height: 26px; display:block; }

        .mrm-sc__body { display:grid; gap:.15rem; }
        .mrm-sc__label { margin:0; font-weight:800; font-size:1.02rem; }
        .mrm-sc__sub { margin:0; font-size:.85rem; color:var(--muted); }

        .mrm-sc__chev {
          margin-left:auto; color:#94a3b8;
          transition: transform .18s ease, color .18s ease;
        }
        .mrm-sc__card:hover .mrm-sc__chev { transform: translateX(3px); color:#1e293b; }
      `}</style>

      <section className="mrm-sc" aria-label="Key service shortcuts">
        <div className="mrm-sc__wrap">
          <header className="mrm-sc__head">
            <h2 className="mrm-sc__title">{title}</h2>
            <p className="mrm-sc__muted">Jump straight to what you need</p>
          </header>

          <div className="mrm-sc__grid">
            {items.map((item, i) => {
              const AccentIcon =
                item.icon ||
                (fallbackIcons[item.label] ? fallbackIcons[item.label] : fallbackIcons.Properties);
              return (
                <a
                  key={item.label}
                  href={item.href || "#"}
                  className="mrm-sc__card"
                  aria-label={`${item.label} — open`}
                >
                  <span
                    className="mrm-sc__iconwrap"
                    style={{ "--accent": accents[i % accents.length] }}
                  >
                    {/* If a React node icon is passed, render it.
                        Otherwise use our fallback SVGs. */}
                    {React.isValidElement(item.icon) ? (
                      React.cloneElement(item.icon, { style: { width: 24, height: 24 } })
                    ) : (
                      <AccentIcon />
                    )}
                  </span>

                  <span className="mrm-sc__body">
                    <strong className="mrm-sc__label">{item.label}</strong>
                    <span className="mrm-sc__sub">Go to {item.label.toLowerCase()}</span>
                  </span>

                  <span className="mrm-sc__chev" aria-hidden="true">➔</span>
                </a>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}

/* Example:
import { PeopleAltOutlined, ManageAccountsOutlined, RealEstateAgent, Home as HomeIcon } from '@mui/icons-material';
const services = [
  { label: 'Buyers', href: '/buyers', icon: <PeopleAltOutlined fontSize="small" /> },
  { label: 'Sellers', href: '/sellers', icon: <ManageAccountsOutlined fontSize="small" /> },
  { label: 'Properties', href: '/properties', icon: <ManageAccountsOutlined fontSize="small" /> },
  { label: 'Landlords', href: '/landlords', icon: <RealEstateAgent fontSize="small" /> },
  { label: 'Tenants', href: '/tenants', icon: <HomeIcon fontSize="small" /> },
];
<ServicesShortcuts items={services} title="Popular links" />
*/
